<div mat-dialog-title class="dialog-header-container">
    <div class="header-content">
        <h2 >{{ title }}</h2>
        <span class="mat-font-body-md">{{description}}</span>
    </div>
</div>
<mat-dialog-content>
    <mat-tab-group [(selectedIndex)]="selectedTabIndex" (selectedIndexChange)="onTabChange($event)" class="search-dialog-tabs">
        <!-- WKT Tab -->
        <mat-tab [tabIndex]="0" *ngIf="canUseMapSelection" label="Map Area">
            <div class="tab-content">
                <div class="method-description">
                    <mat-icon>map</mat-icon>
                    <span>Select an area on the map to generate an image</span>
                </div>

                <div class="map-selection-section">
                    <div class="selection-tools">
                        <div class="tool-cards">
                            <mat-card class="tool-card" [class.selected]="selectedWktMethod === 'square'" (click)="selectWktMethod('square')">
                                <mat-card-content>
                                    <mat-icon svgIcon="box"></mat-icon>
                                    <div class="tool-info">
                                        <div class="tool-title">Rectangle Area</div>
                                        <div class="tool-description">Draw a rectangular area on the map</div>
                                    </div>
                                </mat-card-content>
                            </mat-card>

                            <mat-card class="tool-card" [class.selected]="selectedWktMethod === 'polygon'" (click)="selectWktMethod('polygon')">
                                <mat-card-content>
                                    <mat-icon svgIcon="poly"></mat-icon>
                                    <div class="tool-info">
                                        <div class="tool-title">Custom Area</div>
                                        <div class="tool-description">Draw a custom polygon shape</div>
                                    </div>
                                </mat-card-content>
                            </mat-card>
                        </div>

                        <button matButton="elevated" color="primary" [disabled]="!selectedWktMethod" (click)="startWktSelection()">
                            <mat-icon>edit_location</mat-icon>
                            Start Area Selection
                        </button>
                    </div>

                    <!-- Only show validation status when there's actual content -->
                    <div
                            class="tab-validation-status"
                            [class.valid]="wktValidationState?.isValid"
                            [class.invalid]="wktValidationState?.error"
                            [class.loading]="wktValidationState?.isGenerating"
                            *ngIf="wktValidationState?.geometry || wktValidationState?.isGenerating || wktValidationState?.error">
                        <mat-icon>
                            {{ wktValidationState?.isValid ? "check_circle" : wktValidationState?.error ? "error" : "refresh" }}
                        </mat-icon>
                        <span *ngIf="wktValidationState?.isGenerating">Generating preview from map selection...</span>
                        <span *ngIf="wktValidationState?.isValid">Map area image is ready</span>
                        <span *ngIf="wktValidationState?.error">{{ wktValidationState.error }}</span>
                    </div>
                </div>
            </div>
        </mat-tab>
        <!-- URL Tab -->
        <mat-tab [tabIndex]="1" label="Image URL">
            <div class="tab-content">
                <div class="method-description">
                    <mat-icon>link</mat-icon>
                    <span>Search by entering a direct image URL</span>
                </div>

                <form [formGroup]="urlForm" class="form-section">
                    <mat-form-field appearance="outline" class="full-width">
                        <mat-label>Image URL</mat-label>
                        <input matInput type="url" placeholder="https://example.com/image.jpg" formControlName="url" (input)="validateImageUrl()" />
                        <mat-hint>Enter a direct link to an image file</mat-hint>
                        <mat-error *ngIf="urlForm.get('url')?.errors?.['required']">URL is required</mat-error>
                        <mat-error *ngIf="urlForm.get('url')?.errors?.['pattern']">Please enter a valid URL</mat-error>
                        <mat-error *ngIf="urlValidationState?.error">{{ urlValidationState.error }}</mat-error>
                    </mat-form-field>
                </form>

                <!-- Only show validation status when there's actual content OR errors after interaction -->
                <div
                        class="tab-validation-status"
                        [class.valid]="urlValidationState?.isValid"
                        [class.invalid]="urlValidationState?.error"
                        [class.loading]="urlValidationState?.isValidating"
                        *ngIf="urlForm.get('url')?.value && (urlValidationState?.isValidating || urlValidationState?.isValid || urlValidationState?.error)">
                    <mat-icon>
                        {{ urlValidationState?.isValid ? "check_circle" : urlValidationState?.error ? "error" : "refresh" }}
                    </mat-icon>
                    <span *ngIf="urlValidationState?.isValidating">Validating image URL...</span>
                    <span *ngIf="urlValidationState?.isValid">Image URL is valid and ready</span>
                    <span *ngIf="urlValidationState?.error">{{ urlValidationState.error }}</span>
                </div>
            </div>
        </mat-tab>
        <!-- Upload Tab -->
        <mat-tab [tabIndex]="2" label="Upload Image">
            <div class="tab-content">
                <div class="method-description">
                    <mat-icon>file_upload</mat-icon>
                    <span>Upload an image file from your device</span>
                </div>

                <div class="upload-section">
                    <input #fileInput type="file" class="file-input-hidden" [accept]="acceptedFileTypes" (change)="onFileSelected($event)" />

                    <div
                            class="dropzone d-flex align-items-center justify-content-center text-center"
                            [class.drag-over]="isDragOver"
                            [class.has-image]="uploadedFile"
                            (dragover)="onDragOver($event)"
                            (dragleave)="onDragLeave($event)"
                            (drop)="onDrop($event)"
                            (click)="fileInput.click()">
                        <div *ngIf="!uploadedFile && !isDragOver" class="dropzone-content">
                            <mat-icon class="upload-icon">cloud_upload</mat-icon>
                            <p class="primary-text">Drag & drop your image here</p>
                            <p class="secondary-text">
                                or
                                <span class="link-text">click to browse</span>
                            </p>
                            <div class="file-restrictions">
                                <small>Supported: JPEG, PNG, WebP • Max size: {{ maxFileSizeFormatted }}</small>
                            </div>
                        </div>

                        <div *ngIf="isDragOver" class="dropzone-drag-over">
                            <mat-icon class="drag-icon">backup</mat-icon>
                            <p>Drop your image here</p>
                        </div>

                        <div *ngIf="uploadedFile" class="uploaded-image-preview">
                            <img *ngIf="imagePreview" [src]="imagePreview" alt="Preview" class="image-preview"/>
                            <mat-icon *ngIf="!imagePreview" class="image-preview-error">broken_image</mat-icon>
                            <div class="file-info">
                                <div class="file-name">{{ uploadedFile.name }}</div>
                                <div class="file-details">{{ formatFileSize(uploadedFile.size) }}</div>
                                <button matIconButton color="warn" (click)="removeFile($event)" matTooltip="Remove image">
                                    <mat-icon>close</mat-icon>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Only show validation status when there's actual content -->
                <div
                        class="tab-validation-status"
                        [class.valid]="uploadValidationState?.isValid"
                        [class.invalid]="uploadValidationState?.error"
                        *ngIf="uploadedFile || uploadValidationState?.error">
                    <mat-icon>{{ uploadValidationState?.isValid ? "check_circle" : "error" }}</mat-icon>
                    <span *ngIf="uploadValidationState?.isValid">Image is valid and ready</span>
                    <span *ngIf="uploadValidationState?.error">{{ uploadValidationState.error }}</span>
                </div>
            </div>
        </mat-tab>
    </mat-tab-group>

    <!-- Preview Section - Fixed at bottom, only visible when there's valid content -->
    <div class="preview-section" *ngIf="hasValidContent">
        <div class="preview-header">
            <mat-icon>preview</mat-icon>
            <span>Image Preview</span>
        </div>

        <div class="preview-content">
            <!-- URL Preview -->
            <img
                    *ngIf="currentSearchMethod === ImageSearchMethod.URL && urlValidationState?.previewUrl"
                    [src]="urlValidationState.previewUrl"
                    alt="URL Preview"
                    class="image-preview" />

            <!-- Upload Preview -->
            <img *ngIf="currentSearchMethod === ImageSearchMethod.UPLOAD && imagePreview" [src]="imagePreview" alt="Upload Preview" class="image-preview" />

            <!-- WKT Preview -->
            <img
                    *ngIf="currentSearchMethod === ImageSearchMethod.WKT && wktValidationState?.base64data"
                    [src]="wktValidationState.base64data"
                    alt="Map Preview"
                    class="image-preview"
                    fill />

            <!-- File info -->
            <div class="preview-info" *ngIf="hasValidContent">
                <div class="preview-details">
                    <div class="file-name" *ngIf="currentSearchMethod === ImageSearchMethod.URL">{{ urlForm.get("url")?.value }}</div>
                    <div class="file-name" *ngIf="currentSearchMethod === ImageSearchMethod.UPLOAD">{{ uploadedFile?.name }}</div>
                    <div class="file-size" *ngIf="currentSearchMethod === ImageSearchMethod.UPLOAD && uploadedFile">{{ formatFileSize(uploadedFile.size) }}</div>
                    <div class="file-name" *ngIf="currentSearchMethod === ImageSearchMethod.WKT">Generated from map area</div>
                </div>
            </div>
        </div>
    </div>

    <!-- Global Error Display -->
    <div *ngIf="globalError" class="global-error">
        <mat-icon>error</mat-icon>
        <span>{{ globalError }}</span>
    </div>

</mat-dialog-content>
<mat-dialog-actions align="end">
    <button matButton type="button" (click)="onCancel()" aria-label="Cancel image search">Cancel</button>
    <button
            matButton="filled" color="primary"
            [disabled]="!canPerformSearch || isProcessing"
            [class.loading]="isProcessing"
            (click)="onSearch()"
            aria-label="Search by image">
        <mat-icon *ngIf="canPerformSearch && !isProcessing">search</mat-icon>
        <mat-spinner diameter="20" *ngIf="isProcessing"></mat-spinner>
        <span *ngIf="!isProcessing">Search by Image</span>
        <span *ngIf="isProcessing">Searching...</span>
    </button>
</mat-dialog-actions>
